import React, { useMemo, useState } from 'react';
import Axios from 'axios';
import * as Routes from '@/modules/routes';
import SubmitModal from '@/components/ui/Modal/SubmitModal';
import Select from '@/components/common/ReactSelect5';
import usePrograms from '../usePrograms';

interface AddProgramModalProps {
  modalManager: ModalManager
}

type INewProgramType = 'create' | 'reuse';

type ISelectOption = {
  value: number;
  label: string;
};

type IProgram = {
  id: number;
  code: string;
  full_title_with_edition: string;
  kind: string;
};

const formatOptions = (programs: IProgram[]) => programs.map(program => ({
  kind: program.kind,
  label: `[${program.code}] ${program.full_title_with_edition}`,
  value: program.id,
})).filter(program => program.kind !== 'professional_development');

const AddProgramModal = ({ modalManager }: AddProgramModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [newProgramType, setNewProgramType] = useState<INewProgramType>('reuse');
  const [selected, setSelected] = useState<ISelectOption | null>(null);
  const { isOpen, close } = modalManager;
  const createProgramMode = newProgramType === 'create';
  const reuseProgramMode = newProgramType === 'reuse';

  const { programs, isLoading: isLoadingPrograms } = usePrograms({
    attributes: ['full_title_with_edition', 'id', 'code', 'kind'],
    skip: createProgramMode,
  });
  const options = useMemo(() => formatOptions(programs), [programs]);

  const isValid = () => {
    if (createProgramMode) return true;

    return !!selected;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    setIsLoading(true);
    Axios.post(
      Routes.plato_api_programs_path({ base_program_id: selected?.value }),
    ).then((res) => {
      window.location.replace(Routes.edit_admin_program_path(res.data.data.id));
    }).catch((err) => {
      console.error('error', err);
    }).finally(() => {
      setIsLoading(false);
    });
  };

  return (
    <SubmitModal
      bodyClassName="tw-min-h-[500px]"
      className="tw-text-base"
      close={close}
      formClassName="tw-min-h-[500px]"
      headerText="Add Program"
      isLoading={isLoading}
      isOpen={isOpen}
      submitText="Add Program"
      onSubmit={handleSubmit}
    >
      <p>
        Create a program from scratch or reuse an existing one.
      </p>
      <div className="tw-text-sm tw-ml-2 tw-mb-2">
        <label htmlFor="create-new-program">
          <input
            checked={createProgramMode}
            className="tw-mb-[5px]"
            id="create-new-program"
            name="program"
            type="radio"
            onChange={() => {
              setNewProgramType('create');
            }}
          />
          Create new program
        </label>

        <br />

        <label htmlFor="reuse-program">
          <input
            checked={reuseProgramMode}
            className="tw-mb-[5px]"
            id="reuse-program"
            name="program"
            type="radio"
            onChange={() => {
              setNewProgramType('reuse');
            }}
          />
          Reuse program
        </label>
      </div>

      {reuseProgramMode && (
        <label
          className="tw-ml-8"
          htmlFor="base_program_id"
        >
          <span className="tw-text-sm tw-font-bold">Base Program</span>
          <Select
            isClearable
            showCaret
            inputId="base_program_id"
            isLoading={isLoadingPrograms}
            name="base_program_id"
            options={options}
            value={selected}
            onChange={setSelected}
          />
        </label>
      )}

      {reuseProgramMode && (
        <SubmitModal.SubmitButton
          disabled={!isValid()}
          submitting={isLoading}
          submittingText="Adding Program…"
        >
          Add Program
        </SubmitModal.SubmitButton>
      )}

      {createProgramMode && (
        <SubmitModal.SubmitButton asChild>
          <a className="btn btn--purple" href={Routes.new_admin_program_path()}>
            Add Program
          </a>
        </SubmitModal.SubmitButton>
      )}
    </SubmitModal>
  );
};

export default AddProgramModal;
